import React, { useMemo, useState } from 'react';
import { LayoutChangeEvent, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Svg, {
  Circle,
  Defs,
  G,
  Line,
  LinearGradient,
  Path,
  Stop,
  Text as SvgText,
} from 'react-native-svg';

import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useThemePalette } from '@/config/theme';
import type { MatchShot, TeamRef } from '@/lib/data/provider';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeTeamName } from '@/lib/i18n/footballNames';

const CHART_HEIGHT = 176;
const PAD_TOP = 20; // headroom for the goal glyphs sitting at the top edge
const PAD_BOTTOM = 16;
const PAD_X = 8;
/** Pressure values are roughly −100..100; we cap the wave amplitude here so a
 *  single freak spike can't pin the curve to the chart ceiling. */
const PRESSURE_CAP = 100;
/** Full time is at least 90'. */
const MIN_AXIS_MINUTE = 90;
const HALF_TIME_MINUTE = 45;

interface WavePoint {
  minute: number;
  /** Signed pressure already clamped to ±PRESSURE_CAP. */
  value: number;
}

interface GoalMark {
  minute: number;
  side: 'home' | 'away';
}

/**
 * Resample the raw signed-momentum series to one point per minute across the
 * axis, carrying the last known value forward through gaps and easing between
 * samples with a smoothstep ramp. Many fine, eased points are what let the
 * Catmull-Rom path read as 365's soft wave rather than jagged bars.
 */
function buildWave(
  series: { m: number; v: number }[],
  axisMinute: number,
): WavePoint[] {
  const ordered = series
    .filter((p) => Number.isFinite(p.m) && Number.isFinite(p.v))
    .map((p) => ({
      m: Math.min(Math.max(0, Math.round(p.m)), axisMinute),
      v: Math.max(-PRESSURE_CAP, Math.min(PRESSURE_CAP, p.v)),
    }))
    .sort((a, b) => a.m - b.m);

  if (ordered.length === 0) return [];

  const points: WavePoint[] = [];
  let idx = 0;
  let prevMin = ordered[0].m;
  let prevVal = ordered[0].v;
  for (let m = 0; m <= axisMinute; m += 1) {
    // Advance past every sample whose minute we've now reached.
    while (idx < ordered.length && ordered[idx].m <= m) {
      prevMin = ordered[idx].m;
      prevVal = ordered[idx].v;
      idx += 1;
    }
    if (idx < ordered.length) {
      const next = ordered[idx];
      const span = next.m - prevMin;
      if (span > 0 && m > prevMin) {
        const t = (m - prevMin) / span;
        const eased = t * t * (3 - 2 * t); // smoothstep
        points.push({ minute: m, value: prevVal + (next.v - prevVal) * eased });
        continue;
      }
    }
    points.push({ minute: m, value: prevVal });
  }
  return points;
}

/**
 * 365scores-style MOMENTUM wave. A single react-native-svg node draws a smooth
 * filled wave around a horizontal centre line: the area ABOVE the centre is the
 * HOME team's attacking pressure (volt), the area BELOW is the AWAY team's
 * (coral). The minute axis runs 0' → HT (dashed guide at 45') → FT, with small
 * ball glyphs at the top for the minutes goals were scored (read off the shots,
 * which already carry an `is_goal` flag). Self-hides when no momentum data is
 * present; the path computation is memoised. RTL-safe (the chart itself is
 * always read left→right), light/dark via palette tokens.
 */
export function MomentumChart({
  momentum,
  shots = [],
  homeTeam,
  awayTeam,
}: {
  momentum: { m: number; v: number }[] | null | undefined;
  /** Used only to drop goal-minute ball glyphs; safe to omit. */
  shots?: MatchShot[];
  homeTeam: TeamRef | null;
  awayTeam: TeamRef | null;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { locale, arabicNumerals } = useLocale();
  const [width, setWidth] = useState(0);

  const onLayout = (e: LayoutChangeEvent) => setWidth(e.nativeEvent.layout.width);

  const { wave, axisMinute, goals } = useMemo(() => {
    const series = Array.isArray(momentum) ? momentum : [];
    const lastSeriesMinute = series.reduce((mx, p) => Math.max(mx, p.m), 0);
    const lastGoalMinute = shots.reduce(
      (mx, s) => (s.is_goal ? Math.max(mx, s.minute) : mx),
      0,
    );
    const axis = Math.max(
      MIN_AXIS_MINUTE,
      Math.ceil(Math.max(lastSeriesMinute, lastGoalMinute)),
    );
    const goalMarks: GoalMark[] = shots
      .filter((s) => s.is_goal)
      .map((s) => ({
        minute: Math.min(Math.max(0, Math.round(s.minute)), axis),
        side: s.team_side,
      }))
      .sort((a, b) => a.minute - b.minute);
    return { wave: buildWave(series, axis), axisMinute: axis, goals: goalMarks };
  }, [momentum, shots]);

  // Nothing to plot → render nothing (caller also gates, this is belt-and-braces).
  if (wave.length === 0) return null;

  const innerW = Math.max(0, width - PAD_X * 2);
  const innerH = CHART_HEIGHT - PAD_TOP - PAD_BOTTOM;
  const centreY = PAD_TOP + innerH / 2;
  const halfH = innerH / 2;

  const xFor = (minute: number) =>
    PAD_X + (Math.min(minute, axisMinute) / axisMinute) * innerW;
  // Positive pressure rises ABOVE the centre line (smaller y), negative drops below.
  const yFor = (value: number) =>
    centreY - (value / PRESSURE_CAP) * halfH;

  /**
   * Smooth cubic-Bézier path through the points (Catmull-Rom → Bézier). The same
   * conversion the xG curves use: fine eased points + curved handles = a glassy
   * wave with no visible stair-steps.
   */
  const smoothLine = (pts: { x: number; y: number }[]): string => {
    if (pts.length === 0) return '';
    if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;
    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i += 1) {
      const p0 = pts[i - 1] ?? pts[i];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2] ?? p2;
      const c1x = p1.x + (p2.x - p0.x) / 6;
      const c1y = p1.y + (p2.y - p0.y) / 6;
      const c2x = p2.x - (p3.x - p1.x) / 6;
      const c2y = p2.y - (p3.y - p1.y) / 6;
      d += ` C ${c1x} ${c1y} ${c2x} ${c2y} ${p2.x} ${p2.y}`;
    }
    return d;
  };

  /**
   * One closed area for a single side: the wave clamped so only that side's
   * pressure shows (home keeps the positive lobes, away the negative), then
   * closed back along the centre line so the fill hugs the baseline.
   */
  const areaPath = (side: 'home' | 'away'): string => {
    if (innerW <= 0) return '';
    const pts = wave.map((p) => {
      const v = side === 'home' ? Math.max(0, p.value) : Math.min(0, p.value);
      return { x: xFor(p.minute), y: yFor(v) };
    });
    const line = smoothLine(pts);
    if (!line) return '';
    const lastX = pts[pts.length - 1].x;
    const firstX = pts[0].x;
    return `${line} L ${lastX} ${centreY} L ${firstX} ${centreY} Z`;
  };

  const homeArea = areaPath('home');
  const awayArea = areaPath('away');

  /** A small football glyph used to mark a goal minute at the top of the chart. */
  const Ball = ({ cx, cy, tint }: { cx: number; cy: number; tint: string }) => (
    <G>
      <Circle cx={cx} cy={cy} r={7} fill={tint} opacity={0.26} />
      <Circle cx={cx} cy={cy} r={4.5} fill={palette.text} stroke={tint} strokeWidth={1.4} />
      <Circle cx={cx} cy={cy} r={1.4} fill={palette.deep} />
      <Circle cx={cx - 2.1} cy={cy + 1.6} r={0.9} fill={palette.deep} opacity={0.7} />
      <Circle cx={cx + 2.1} cy={cy + 1.6} r={0.9} fill={palette.deep} opacity={0.7} />
    </G>
  );

  return (
    <Card className="overflow-hidden">
      <View className="flex-row items-center justify-between px-4 pb-1 pt-3">
        <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-muted">
          {t('match.momentumWave')}
        </AppText>
      </View>

      {/* Crests flank the wave so it's obvious which lobe is whose. */}
      <View className="flex-row items-center justify-between px-4 pb-1 pt-1">
        <View className="flex-1 flex-row items-center gap-1.5">
          <TeamCrest uri={homeTeam?.logo_url} name={localizeTeamName(homeTeam, locale)} size="xs" />
          <AppText variant="micro" weight="bold" className="text-volt-bright">
            {localizeTeamName(homeTeam, locale, true)}
          </AppText>
        </View>
        <View className="flex-1 flex-row items-center justify-end gap-1.5">
          <AppText variant="micro" weight="bold" style={{ color: palette.teamAway }}>
            {localizeTeamName(awayTeam, locale, true)}
          </AppText>
          <TeamCrest uri={awayTeam?.logo_url} name={localizeTeamName(awayTeam, locale)} size="xs" />
        </View>
      </View>

      {/* The wave is always read left→right (minute axis), even in RTL. */}
      <View
        className="px-4 pb-0.5"
        style={{ height: CHART_HEIGHT, direction: 'ltr' }}
        onLayout={onLayout}
      >
        {width > 0 ? (
          <Svg width={innerW + PAD_X * 2} height={CHART_HEIGHT}>
            <Defs>
              <LinearGradient id="momHome" x1="0" y1="0" x2="0" y2="1">
                <Stop offset="0" stopColor={palette.volt} stopOpacity={0.42} />
                <Stop offset="1" stopColor={palette.volt} stopOpacity={0.05} />
              </LinearGradient>
              <LinearGradient id="momAway" x1="0" y1="1" x2="0" y2="0">
                <Stop offset="0" stopColor={palette.teamAway} stopOpacity={0.42} />
                <Stop offset="1" stopColor={palette.teamAway} stopOpacity={0.05} />
              </LinearGradient>
            </Defs>

            {/* Half-time dashed guide. */}
            <Line
              x1={xFor(HALF_TIME_MINUTE)}
              y1={PAD_TOP - 4}
              x2={xFor(HALF_TIME_MINUTE)}
              y2={PAD_TOP + innerH}
              stroke={palette.pitchLine}
              strokeWidth={1}
              strokeDasharray="3 5"
              opacity={0.26}
            />

            {/* Filled lobes: away below, home above. */}
            <Path d={awayArea} fill="url(#momAway)" stroke="none" />
            <Path d={homeArea} fill="url(#momHome)" stroke="none" />

            {/* Wave outlines, tinted per side, drawn over the fills. */}
            <Path
              d={smoothLine(
                wave.map((p) => ({ x: xFor(p.minute), y: yFor(Math.min(0, p.value)) })),
              )}
              stroke={palette.teamAway}
              strokeWidth={1.75}
              fill="none"
              opacity={0.7}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <Path
              d={smoothLine(
                wave.map((p) => ({ x: xFor(p.minute), y: yFor(Math.max(0, p.value)) })),
              )}
              stroke={palette.volt}
              strokeWidth={2}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* The centre (kickoff-balance) line, drawn last so it reads on top. */}
            <Line
              x1={PAD_X}
              y1={centreY}
              x2={innerW + PAD_X}
              y2={centreY}
              stroke={palette.muted}
              strokeWidth={1}
              opacity={0.35}
            />

            {/* HT label above the dashed guide. */}
            <SvgText
              x={xFor(HALF_TIME_MINUTE)}
              y={PAD_TOP - 8}
              fill={palette.muted}
              fontSize={8.5}
              fontWeight="700"
              textAnchor="middle"
            >
              {t('match.halfTime')}
            </SvgText>

            {/* Goal glyphs at the top, tinted by the scoring side, with a faint
                stem dropping toward the centre line. */}
            {goals.map((g, i) => {
              const tint = g.side === 'home' ? palette.volt : palette.teamAway;
              const gx = xFor(g.minute);
              return (
                <G key={`goal-${g.side}-${g.minute}-${i}`}>
                  <Line
                    x1={gx}
                    y1={PAD_TOP - 1}
                    x2={gx}
                    y2={centreY}
                    stroke={tint}
                    strokeWidth={1}
                    strokeDasharray="2 3"
                    opacity={0.4}
                  />
                  <Ball cx={gx} cy={PAD_TOP - 4} tint={tint} />
                </G>
              );
            })}
          </Svg>
        ) : null}
      </View>

      {/* Minute axis: 0' (kickoff) … FT (full time / last minute). */}
      <View className="flex-row items-center justify-between px-4 pb-3" style={{ direction: 'ltr' }}>
        <AppText variant="micro" weight="medium" tabular className="text-muted">
          {localizeDigits('0', arabicNumerals)}&apos;
        </AppText>
        <AppText variant="micro" weight="bold" tabular className="text-muted">
          {t('match.fullTime')}
        </AppText>
      </View>

      <View className="border-t border-line px-4 py-2">
        <AppText variant="micro" className="text-muted">
          {t('match.momentumWaveHint')}
        </AppText>
      </View>
    </Card>
  );
}
